import {
  Box as MuiBox,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  ThemeProvider,
  createTheme
} from "@mui/material";
import React, { useState } from "react";
import { useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import PeopleIcon from "../../shared/icons/People";
import PeopleActiveIcon from "../../shared/icons/PeopleActive";
import colors from "../themes/colors";
import LogoSmallIcon from "../assets/logo/MachineIdLogoSmall";
import LogoBigIcon from "../assets/logo/MachineIdLogoBig";
import SupplierCenter from "../icons/SupplierCenter";
import SupplierCenterActive from "../icons/SupplierCenterActive";
import DiplomatIcon from "../icons/Diplomat";
import DiplomatActiveIcon from "../icons/DiplomatActive";
import Box from "../icons/Box";
import BoxActive from "../icons/BoxActive";
import accountContext from "../../account/accountContext";
import { Roles } from "../../account/models/AccountModels";
import PaperIcon from "../assets/icons/PaperIcon";
import PaperMoneyIcon from "../assets/icons/PaperMoneyIcon";
import GearIcon from "../icons/GearIcon";
import useTranslation from "../hooks/useTranslation";
import { dt } from "../themes/redesignTokens";

interface MenuItemProps {
  menuItem: MenuItemDefinition;
  toggleDrawer: boolean;
  onMouseOver: (event: any, menuItem: MenuItemDefinition) => void;
  onMouseLeave: () => void;
}

const MenuItem = ({ menuItem, toggleDrawer, onMouseOver, onMouseLeave }: MenuItemProps) => {
  if (menuItem.type === "divider") {
    return (
      <MuiBox sx={{ padding: "12px" }}>
        <Divider sx={{ borderColor: "#e5e7eb" }} variant="middle" />
        {toggleDrawer && (
          <MuiBox className="text-14-regular text-left" sx={{ mt: 1, ml: 2 }}>
            <MuiBox component="span" sx={{ color: dt.blue }}>{menuItem.title}</MuiBox>
          </MuiBox>
        )}
      </MuiBox>
    );
  }

  return (
    <NavLink key={menuItem.navLink} to={menuItem.navLink || "/"}>
      {({ isActive }) => (
        <>
          <ListItem sx={{
            boxShadow: isActive ? "inset 4px 0 0 0 #fff" : "none",
            mb: menuItem.items && toggleDrawer ? 0 : 2
          }} disablePadding>
            <ListItemButton
              sx={{ padding: toggleDrawer ? "8px 0 8px 16px" : "12px 16px" }}
              onMouseOver={(event) => onMouseOver(event, menuItem)}
              onMouseLeave={onMouseLeave}
            >
              <ListItemIcon sx={{ minWidth: "30px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                {isActive ? menuItem.activeIcon : menuItem.icon}
              </ListItemIcon>
              {toggleDrawer && (
                <ListItemText
                  className="ml-3 text-14-medium"
                  primary={menuItem.title}
                  sx={{
                    color: isActive ? colors.primary.white1 : "",
                    whiteSpace: "nowrap",
                    "& .MuiTypography-root": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>

          {toggleDrawer && !!menuItem.items && (
            <>
              {menuItem.items.map((subMenuItem) => (
                <NavLink key={subMenuItem.navLink} to={subMenuItem.navLink || "/"}>
                  {({ isActive }) => (
                    <ListSubheader sx={{
                      background: "none",
                    }}>
                      <ListItemButton sx={{
                        paddingTop: "4px",
                        paddingBottom: "4px"
                      }}>
                        <ListItemIcon sx={{ minWidth: "30px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                          {isActive ? subMenuItem.activeIcon : subMenuItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          className="text-14-medium"
                          primary={subMenuItem.title}
                          sx={{
                            color: isActive ? colors.primary.white1 : "",
                            whiteSpace: "nowrap",
                            "& .MuiTypography-root": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListSubheader>
                  )}
                </NavLink>
              ))}
              <MuiBox sx={{ mb: 1 }} />
            </>
          )}
        </>
      )}
    </NavLink>
  );
};

interface MenuItemDefinition {
  type?: "default" | "divider";
  title?: string;
  navLink?: string;
  icon?: any;
  activeIcon?: any;
  requiredUserRole?: string;
  items?: MenuItemDefinition[];
}

const Menu = () => {
  const { translate } = useTranslation(["layout", "common"]);
  const [toggleDrawer, setToggleDrawer] = React.useState(false);
  const handleDrawerToggle = useCallback(() => setToggleDrawer(!toggleDrawer), [toggleDrawer]);

  const user = accountContext.user;

  const { pathname } = useLocation();
  const isAdminPath = pathname.includes("admin");

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [subMenu, setSubMenu] = useState<MenuItemDefinition[] | undefined>();

  if (!isAdminPath) {
    return null;
  }

  const adminMenuItems: MenuItemDefinition[] = [
    {
      title: translate("layout:admin.offers"),
      navLink: "/admin/offers",
      icon: <PaperMoneyIcon color={colors.secondary.gray40} />,
      activeIcon: <PaperMoneyIcon />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.rfqs"),
      navLink: "/admin/rfqs",
      icon: <PaperIcon color={colors.secondary.gray40} />,
      activeIcon: <PaperIcon />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.users"),
      navLink: "/admin/users",
      icon: <PeopleIcon />,
      activeIcon: <PeopleActiveIcon />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.customers"),
      navLink: "/admin/customers",
      icon: <DiplomatIcon />,
      activeIcon: <DiplomatActiveIcon />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.machines"),
      navLink: "/admin/machines",
      icon: <GearIcon color={colors.secondary.gray40} />,
      activeIcon: <GearIcon />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.leads"),
      navLink: "/admin/leads",
      icon: <LeaderboardOutlinedIcon sx={{ color: colors.secondary.gray40 }} />,
      activeIcon: <LeaderboardOutlinedIcon sx={{ color: dt.blue }} />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.catalog"),
      navLink: "/admin/catalog",
      icon: <Box />,
      activeIcon: <BoxActive />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    },
    {
      title: translate("layout:admin.inventory"),
      navLink: "/admin/inventory",
      icon: <SupplierCenter />,
      activeIcon: <SupplierCenterActive />,
      requiredUserRole: Roles.SYSTEM_ADMINISTRATOR
    }
  ];


  let menuItemHoverTimeoutId: any;

  const handleMenuItemMouseOver = (event: any, menuItem: MenuItemDefinition) => {
    if (toggleDrawer || !menuItem.items?.length || anchorEl === event.currentTarget) {
      return;
    }

    setAnchorEl(event.currentTarget);
    setSubMenu(menuItem.items);
  };

  const handleMenuItemMouseLeave = () => {
    if (menuItemHoverTimeoutId) {
      clearTimeout(menuItemHoverTimeoutId);
    }

    menuItemHoverTimeoutId = setTimeout(() => {
      handleSubMenuClose();
    });
  };

  const handleSubMenuEnter = () => {
    if (menuItemHoverTimeoutId) {
      clearTimeout(menuItemHoverTimeoutId);
    }
  };

  const handleSubMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [...adminMenuItems];

  const theme = createTheme({
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            ".MuiListItemText-root .MuiTypography-root": {
              fontWeight: 500
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            backgroundColor: colors.secondary.gray20
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: "#FFF",
            backgroundColor: colors.secondary.gray20,
            fontSize: "14px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": {
              color: `${dt.blue} !important`,
              backgroundColor: `${colors.secondary.gray20} !important`
            }
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            pointerEvents: "none"
          }
        }
      }
    }
  });

  return (
    <>
      <ThemeProvider theme={theme}>
        <Drawer
          variant="permanent"
          open={toggleDrawer}
          className={toggleDrawer ? "drawer-open" : "drawer-close"}
          sx={{
            zIndex: 1260
          }}
        >
          <div className="ps-2 pe-4 py-2">
            <Button component={Link} to="/">
              {toggleDrawer ? <LogoBigIcon /> : <LogoSmallIcon />}
            </Button>
          </div>

          <List sx={{ display: "flex", flexDirection: "column", mt: toggleDrawer ? "60px" : "75px" }}>
            {menuItems
              .filter((x) => !x.requiredUserRole || x.requiredUserRole === user?.role)
              .map((menuItem, index) => (
                <MenuItem
                  key={index}
                  menuItem={menuItem}
                  toggleDrawer={toggleDrawer}
                  onMouseOver={handleMenuItemMouseOver}
                  onMouseLeave={handleMenuItemMouseLeave}
                />
              ))}
          </List>

          <Divider />

          <button onClick={handleDrawerToggle} className="sidebar-button">
            {toggleDrawer ? <KeyboardDoubleArrowLeftOutlinedIcon /> : <KeyboardDoubleArrowRightOutlinedIcon />}
          </button>
        </Drawer>

        <MuiMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          onClose={handleSubMenuClose}
          MenuListProps={{
            onMouseEnter: handleSubMenuEnter,
            onMouseLeave: handleSubMenuClose,
            sx: { pointerEvents: "auto" }
          }}
        >
          {subMenu?.map((subMenuItem) => (
            <NavLink key={subMenuItem.navLink} to={subMenuItem.navLink || "/"}>
              <MuiMenuItem onClick={handleSubMenuClose}>{subMenuItem.title}</MuiMenuItem>
            </NavLink>
          ))}
        </MuiMenu>
      </ThemeProvider>
    </>
  );
};

export default Menu;